import React, { useMemo } from 'react'
import { Box, Checkbox, FormControlLabel, Typography, styled, useTheme } from '@mui/material'
import { AddOutlined, DeleteOutline, GridViewOutlined, NearMeOutlined, UploadFileOutlined } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MUIThemeColors } from 'src/mui-theme'
import { GetRGBA, getThemeColor } from 'src/utils'
import { SegmentGroup, TOOLBAR_HEIGHT, segmentSx } from '../components/positions/configure/DrawingToolbar'
import { getConfigurePalette } from '../components/positions/configure/configurePalette'
import { formatVersionLabel } from './projectVersion.action'
import { RevisionConfigure, RevisionMapView, RevisionMode, RevisionTool } from './useRevisionConfigure'

const ICON_SIZE = { fontSize: 15 }

interface RevisionToolbarProps {
  revision: RevisionConfigure
}

// Revision controls above the drawing: what a click does, and the Expand grid and Upload Olex modes, which take the
// workspace over until they are applied or cancelled.
export const RevisionToolbar: React.FC<RevisionToolbarProps> = ({ revision }) => {
  const { t: translate } = useTranslation('projects')
  const theme = useTheme()
  const palette = useMemo(() => getConfigurePalette(theme), [theme])
  const { changes, highlight, setHighlight, view, setView, project, tool, setTool, mode, setMode } = revision
  const isAfter = view === 'after'

  const tools: { key: RevisionTool; label: string; icon: React.ReactNode }[] = [
    {
      key: 'select',
      label: translate('revision-tool-select', { ns: 'projects' }),
      icon: <NearMeOutlined sx={ICON_SIZE} />,
    },
    { key: 'addLine', label: translate('revision-add-line', { ns: 'projects' }), icon: <AddOutlined sx={ICON_SIZE} /> },
    { key: 'remove', label: translate('revision-remove', { ns: 'projects' }), icon: <DeleteOutline sx={ICON_SIZE} /> },
  ]
  const views: { key: RevisionMapView; label: string }[] = [
    { key: 'after', label: formatVersionLabel(project.version?.versionNumber ?? 0) },
    { key: 'before', label: formatVersionLabel(changes?.baseVersionNumber ?? 0) },
  ]

  const modes: { key: Exclude<RevisionMode, 'configure'>; label: string; icon: React.ReactNode }[] = [
    {
      key: 'expand',
      label: translate('revision-expand-grid', { ns: 'projects' }),
      icon: <GridViewOutlined sx={ICON_SIZE} />,
    },
    {
      key: 'olex',
      label: translate('revision-olex-upload', { ns: 'projects' }),
      icon: <UploadFileOutlined sx={ICON_SIZE} />,
    },
  ]
  const inMode = mode !== 'configure'

  const segment = (active: boolean, label: string, onClick: () => void, icon?: React.ReactNode, disabled?: boolean) => (
    <GridActionButton
      key={label}
      aria-pressed={active}
      onClick={onClick}
      disabled={disabled}
      buttonIcon={icon}
      buttonText={label}
      bgColor={active ? palette.primary : undefined}
      textColor={active ? palette.paper : palette.primary}
      buttonTextProps={{ fontSize: 12 }}
      height={TOOLBAR_HEIGHT}
      border={0}
      shadowWeight={null}
      sx={{ ...segmentSx, color: active ? palette.paper : palette.primary }}
    />
  )

  return (
    <Strip>
      <Typography variant="caption" fontWeight={700} letterSpacing={0.6} color={palette.primary}>
        {formatVersionLabel(project.version?.versionNumber ?? 0).toUpperCase()}
      </Typography>
      <SegmentGroup>
        {tools.map(item =>
          segment(
            isAfter && !inMode && tool === item.key,
            item.label,
            () => setTool(item.key),
            item.icon,
            !isAfter || inMode,
          ),
        )}
      </SegmentGroup>
      <SegmentGroup>
        {modes.map(item =>
          segment(
            mode === item.key,
            item.label,
            () => setMode(mode === item.key ? 'configure' : item.key),
            item.icon,
            !isAfter,
          ),
        )}
      </SegmentGroup>
      <Box flex={1} />
      {inMode && (
        <Typography variant="caption" color={palette.muted}>
          {translate(mode === 'expand' ? 'revision-mode-note-expand' : 'revision-mode-note-olex', { ns: 'projects' })}
        </Typography>
      )}
      {!inMode && (
        <>
          <FormControlLabel
            sx={{ marginLeft: 0 }}
            control={<Checkbox size="small" checked={highlight} onChange={e => setHighlight(e.target.checked)} />}
            label={
              <Typography variant="caption" fontWeight={600}>
                {translate('revision-highlight', { ns: 'projects' })}
              </Typography>
            }
          />
          <SegmentGroup>
            {views.map(item => segment(view === item.key, item.label, () => setView(item.key)))}
          </SegmentGroup>
        </>
      )}
    </Strip>
  )
}

export default RevisionToolbar

const Strip = styled(Box)(({ theme }) => {
  const accent = getThemeColor(theme, MUIThemeColors.primaryMain)
  return {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: 6,
    border: `1px solid ${GetRGBA(accent, 0.25)}`,
    backgroundColor: GetRGBA(accent, 0.05),
  }
})
